import React, { useState, useEffect } from 'react';
import { dataService } from '../services/dataService';
import { useWorkspaceRole } from '../contexts/WorkspaceRoleContext';
import { setWorkspaceAiKey } from '../services/aiKey';


const SERVICES = [
  {
    id: 'anthropic', name: 'Claude (Anthropic)', icon: '🧠',
    // Handled apart from the other connectors: this credential SPENDS money, so
    // it is verified and stored through a callable, kept out of the workspace
    // document the other connectors share, and never returned to a client.
    isAiKey: true,
    desc: 'Run your agents on your own Anthropic account. Usage is billed to you, not to Projectly, and you keep your own limits and controls.',
    fields: [
      { label: 'Anthropic API key', key: 'token', type: 'password', placeholder: 'sk-ant-…' },
    ],
    howTo: {
      where: 'console.anthropic.com → Settings → API keys',
      steps: [
        'Sign in at console.anthropic.com. This is separate from a Claude Pro or Max subscription — a subscription does not include API access, and API usage is billed on its own.',
        'Add a payment method under Billing if you have not already.',
        'Open API keys and create a key.',
        'Copy it now and paste it below. It starts with "sk-ant-".',
      ],
      note: 'Projectly checks the key with Anthropic before saving it, so you will know immediately if it is wrong. It is never shown again afterwards — replace it rather than reading it back.',
    },
  },
  {
    id: 'taskdojo', name: 'taskDojo', icon: '🥷',
    howTo: {
      where: 'taskDojo → Settings → Connectors',
      steps: [
        'Open taskDojo on the web and sign in.',
        'Go to Settings → Connectors and create a token.',
        'Copy it straight away — it is shown once and cannot be retrieved later.',
        'Paste it below. It starts with "td_".',
      ],
      note: 'The token acts for your taskDojo account only, and every read is filtered to it server-side. Once saved, add a Collection to your pipeline and set its Source to taskDojo — you do not need a Connection node.',
    },
    desc: "Read and update a taskDojo user's tasks directly — status, progress, comments and the rest. Nothing is copied, so what you see is what is actually there.",
    fields: [
      { label: 'taskDojo token', key: 'token', type: 'password', placeholder: 'td_…' },
    ],
  },
  {
    id: 'github', name: 'GitHub', icon: '🐙',
    howTo: {
      where: 'GitHub → Settings → Developer settings → Personal access tokens',
      steps: [
        'Open github.com → your avatar → Settings.',
        'Scroll to Developer settings → Personal access tokens → Fine-grained tokens.',
        'Generate a token, granting only the repositories and permissions you need.',
        'Copy it immediately — GitHub shows it once.',
      ],
      note: 'Prefer a fine-grained token limited to one repository over a classic token with full account access.',
    },
    desc: 'Let agents create issues, PRs and commits from Chat and the Inbox, or call the GitHub API from an HTTP-request action. (Dedicated GitHub pipeline action nodes are coming soon.)',
    fields: [
      { label: 'Personal Access Token', key: 'token', type: 'password' },
      { label: 'Default Repository (owner/repo)', key: 'repo', type: 'text', placeholder: 'e.g. myorg/myapp' },
      { label: 'Default Branch', key: 'branch', type: 'text', placeholder: 'main' },
    ],
  },
  {
    id: 'email', name: 'Email', icon: '📧',
    desc: 'Send automated notifications via Gmail or SMTP. Used by agents to notify humans.',
    fields: [
      { label: 'SMTP Host', key: 'host' },
      { label: 'Username', key: 'user' },
      { label: 'App Password', key: 'pass', type: 'password' },
    ],
  },
  {
    id: 'slack', name: 'Slack', icon: '💬',
    howTo: {
      where: 'Slack → api.slack.com/apps → your app → OAuth & Permissions',
      steps: [
        'Go to api.slack.com/apps and open (or create) an app for your workspace.',
        'Under OAuth & Permissions, add the scopes you need (e.g. chat:write).',
        'Install the app to the workspace.',
        'Copy the Bot User OAuth Token — it starts with "xoxb-".',
      ],
    },
    desc: 'Post messages and alerts to Slack channels when events occur in your pipeline.',
    fields: [
      { label: 'Webhook URL', key: 'webhook' },
      { label: 'Bot Token', key: 'token', type: 'password' },
    ],
  },
  {
    id: 'openai', name: 'OpenAI', icon: '🤖',
    howTo: {
      where: 'OpenAI → platform.openai.com → API keys',
      steps: [
        'Sign in at platform.openai.com.',
        'Open API keys and create a new secret key.',
        'Copy it now — it is not shown again.',
      ],
      note: 'Usage is billed to the account that owns the key.',
    },
    desc: 'Connect additional AI models (GPT-4o, o1) to specific agents for specialist tasks.',
    fields: [{ label: 'API Key', key: 'apiKey', type: 'password' }],
  },
  {
    id: 'instagram', name: 'Instagram', icon: '📸',
    desc: 'Auto-post content and stories via the Instagram Graph API.',
    fields: [
      { label: 'Access Token', key: 'token', type: 'password' },
      { label: 'Page ID', key: 'pageId' },
    ],
  },
  {
    id: 'canva', name: 'Canva', icon: '🎨',
    desc: 'Generate and export design assets. Link with Marketing agents for automated content creation.',
    fields: [{ label: 'API Key', key: 'apiKey', type: 'password' }],
  },
  {
    id: 'gdrive', name: 'Google Drive', icon: '📁',
    desc: 'Store, share, and manage files. Agents can read and write documents automatically.',
    fields: [{ label: 'OAuth Token', key: 'token', type: 'password' }],
  },
  {
    id: 'stripe', name: 'Stripe', icon: '💳',
    howTo: {
      where: 'Stripe → Developers → API keys',
      steps: [
        'Open the Stripe dashboard → Developers → API keys.',
        'Use a restricted key with only the permissions this workspace needs.',
        'Copy the key and paste it below.',
      ],
      note: 'Start with a test-mode key until you have seen the pipeline behave.',
    },
    desc: 'Allow agents to query payment status, issue refunds, or trigger billing events.',
    fields: [
      { label: 'Secret Key', key: 'secret', type: 'password' },
      { label: 'Webhook Secret', key: 'whSecret', type: 'password' },
    ],
  },
];

/** Where taskDojo answers. Its API resolves the account from the token. */
const TASKDOJO_API = 'https://us-central1-taskdojo-dev.cloudfunctions.net/api/api/v1';

/**
 * Prove a taskDojo token works before storing it.
 *
 * Saving an unverified credential is how someone ends up "connected" to
 * nothing: a token with a typo, or one that has since been revoked, saves
 * silently, shows a green card, and then fails the first time an automation
 * runs — far from the screen where it could be fixed. One call settles it, and
 * it comes back with whose account the token is for, which is worth showing:
 * it is the difference between "saved" and "connected to Chris".
 */
async function verifyTaskdojoToken(token) {
  if (!token || !token.startsWith('td_')) {
    return { ok: false, message: 'That does not look like a taskDojo token — they start with "td_".' };
  }
  try {
    const res = await fetch(`${TASKDOJO_API}/me`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const body = await res.text();
    let parsed;
    try {
      parsed = JSON.parse(body);
    } catch {
      return { ok: false, message: 'taskDojo could not be reached.' };
    }
    if (!res.ok) {
      return { ok: false, message: parsed.error || `taskDojo returned ${res.status}.` };
    }
    return { ok: true, account: parsed.displayName || parsed.email || parsed.id };
  } catch {
    return { ok: false, message: 'Could not reach taskDojo. Check your connection.' };
  }
}

function ConnectModal({ service, existingValues, workspaceId, onClose }) {
  const [values, setValues] = useState(existingValues || {});
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState('');

  const handleSave = async (e) => {
    e.preventDefault();
    if (!workspaceId) { setError('No workspace selected.'); return; }
    setSaving(true);
    setError('');
    try {
      if (service.isAiKey) {
        const res = await setWorkspaceAiKey(workspaceId, (values.token || '').trim());
        if (!res.success) { setError(res.error || 'Anthropic rejected that key.'); setSaving(false); return; }
        onClose(true);
        return;
      }
      // Checked before it is stored, not after it fails in a pipeline.
      if (service.id === 'taskdojo') {
        const check = await verifyTaskdojoToken((values.token || '').trim());
        if (!check.ok) { setError(check.message); setSaving(false); return; }
      }
      const current = await dataService.getConnections(workspaceId);
      await dataService.saveConnections(workspaceId, {
        ...current,
        [service.id]: values,
      });
      onClose(true);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
      setSaving(false);
    }
  };

  return (
    <div className="modal-backdrop" onClick={(e) => { if (e.target === e.currentTarget) onClose(false); }}>
      <div className="modal-box">
        <div className="modal-header">
          <span className="modal-icon">{service.icon}</span>
          <h3 className="modal-title">Connect {service.name}</h3>
          <button className="modal-close" onClick={() => onClose(false)}>×</button>
        </div>
        <p className="modal-desc">{service.desc}</p>
        {service.howTo && (
          <div className="connector-howto">
            <div className="connector-howto-title">ⓘ Where to find this</div>
            <div className="connector-howto-where">{service.howTo.where}</div>
            <ol className="connector-howto-steps">
              {service.howTo.steps.map((st, i) => <li key={i}>{st}</li>)}
            </ol>
            {service.howTo.note && <div className="connector-howto-note">{service.howTo.note}</div>}
          </div>
        )}
        <div className="modal-secure-note">
          {service.isAiKey
            ? '🔑 This key can spend money on your Anthropic account. It is stored for this workspace, never shown again, and never sent to the browser. Set a spend limit in the Anthropic console as a backstop.'
            : '🔑 Credentials are stored in your workspace and are readable by its members. Prefer a scoped, revocable token over a full-access one.'}
        </div>
        {error && <div style={{ color: '#ef4444', fontSize: 13, marginBottom: 8 }}>{error}</div>}
        <form onSubmit={handleSave} className="modal-form">
          {service.fields.map(f => (
            <div key={f.key} className="panel-field">
              <label className="panel-label">{f.label}</label>
              <input
                className="panel-input"
                type={f.type || 'text'}
                placeholder={f.type === 'password' ? '••••••••••••' : (f.placeholder || f.label)}
                value={values[f.key] || ''}
                onChange={e => setValues(p => ({ ...p, [f.key]: e.target.value }))}
              />
            </div>
          ))}
          <div className="modal-actions">
            <button type="button" className="panel-delete-btn" onClick={() => onClose(false)}>Cancel</button>
            <button type="submit" className="panel-save-btn" style={{ width: 'auto', padding: '8px 20px' }} disabled={saving}>
              {saving ? 'Saving…' : 'Save & Connect'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function ConnectionsView({ workspaceId }) {
  const { hasCapability } = useWorkspaceRole();
  const canManage = hasCapability('edit_pipeline');
  const [storedConns, setStoredConns] = useState({});
  const [modal,       setModal]       = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    if (!workspaceId) { setLoading(false); return; }
    dataService.getConnections(workspaceId)
      .then(c => { setStoredConns(c || {}); setLoading(false); })
      .catch(() => setLoading(false));
  }, [workspaceId]);

  const handleDisconnect = async (serviceId) => {
    if (!workspaceId) return;
    const updated = { ...storedConns };
    delete updated[serviceId];
    await dataService.saveConnections(workspaceId, updated);
    setStoredConns(updated);
  };

  if (loading) return <div className="pipeline-loading">Loading connections…</div>;

  return (
    <div className="connections-view">
      <div className="connections-header">
        <h2 className="connections-title">Connectors</h2>
        <p className="connections-subtitle">
          Connect external services to your pipeline agents. Each connector unlocks new capabilities.
        </p>
        {!workspaceId && (
          <div style={{ color: '#f59e0b', fontSize: 13, marginTop: 8 }}>
            Select a workspace to manage connections.
          </div>
        )}
        {workspaceId && !canManage && (
          <div style={{ color: '#92400e', background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 8, padding: '10px 12px', fontSize: 12, fontWeight: 600, marginTop: 10 }}>
            🔒 You have read-only access. Managing connections requires the pipeline-edit permission.
          </div>
        )}
      </div>

      <div className="connections-grid">
        {SERVICES.map(service => {
          const isConnected = !!(storedConns[service.id]
            && Object.keys(storedConns[service.id]).length > 0);
          return (
            <div key={service.id} className={`connection-card ${isConnected ? 'connected' : ''}`}>
              <div className="conn-card-header">
                <span className="conn-card-icon">{service.icon}</span>
                <div className="conn-card-title-wrap">
                  <span className="conn-card-name">{service.name}</span>
                  <span className={`conn-status-pill ${isConnected ? 'on' : 'off'}`}>
                    {isConnected ? 'Connected' : 'Not connected'}
                  </span>
                </div>
              </div>
              <p className="conn-card-desc">{service.desc}</p>
              <div className="conn-card-footer">
                {isConnected ? (
                  <>
                    <button className="conn-manage-btn" onClick={() => setModal(service)} disabled={!canManage}>Manage</button>
                    {canManage && (
                      <button className="conn-disconnect-btn" onClick={() => handleDisconnect(service.id)}>Disconnect</button>
                    )}
                  </>
                ) : (
                  <button
                    className="conn-connect-btn"
                    onClick={() => setModal(service)}
                    disabled={!workspaceId || !canManage}
                  >
                    Connect
                  </button>
                )}
              </div>
            </div>
          );
        })}
      </div>

      {modal && (
        <ConnectModal
          service={modal}
          existingValues={storedConns[modal.id] || {}}
          workspaceId={workspaceId}
          onClose={(didConnect) => {
            if (didConnect) {
              // Reload from Firestore so we show the real saved state
              dataService.getConnections(workspaceId).then(c => setStoredConns(c || {}));
            }
            setModal(null);
          }}
        />
      )}
    </div>
  );
}
